// Copyright (c) StrangeBee 2023

import React, { ReactElement, useMemo } from 'react';
import { useLocalMessage } from '@Helpers/locale';
import { FormatDate } from '@Molecules/Format';
import { ColumnsType } from 'antd/lib/table';
import { columnsRenderer } from '@Hooks/useConfigList/helper/computeHeader';
import { THList } from '@Molecules/THList';
import { CaseReportAttachmentOutput } from 'thehive-sdk';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { ICONS } from '@Constants/iconsApp';
import { formatFileSize } from '@Helpers/stats';
import ExternalLink from '@Atoms/ExternalLink/ExternalLink';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { copyToClipboard } from '@Helpers/copy';
import { ColumnTitle } from '@Atoms/ColumnTitle/ColumnTitle';

interface AttachmentReportsListProps {
    dataSource: CaseReportAttachmentOutput[] | undefined;
    onDelete: ({ id }: { id: string }) => void;
    status: string;
    hasPermissions: boolean;
    getDownloadUrl: (attachmmentId: string) => string;
}

export const AttachmentReportsList = ({
    dataSource,
    onDelete,
    status,
    hasPermissions,
    getDownloadUrl,
}: AttachmentReportsListProps): ReactElement | null => {
    const i = useLocalMessage();

    const columns = useMemo<ColumnsType<CaseReportAttachmentOutput>>(
        () =>
            columnsRenderer([
                {
                    title: <ColumnTitle>{i('case.attachment.table.filename')}</ColumnTitle>,
                    className: 'case-attachment-list__column-filename custom-table__cell',
                    render: ({ filename }): ReactElement => <span>{filename}</span>,
                },
                {
                    title: <ColumnTitle>{i('case.attachment.table.type')}</ColumnTitle>,
                    className: 'case-attachment-list__column-type custom-table__cell',
                    render: ({ contentType }): ReactElement => <span>{contentType}</span>,
                },
                {
                    title: <ColumnTitle>{i('case.attachment.table.size')}</ColumnTitle>,
                    className: 'case-attachment-list__column-size custom-table__cell',
                    render: ({ size }): ReactElement => <span>{formatFileSize(size)}</span>,
                },
                {
                    title: <ColumnTitle>{i('case.attachment.table.date')}</ColumnTitle>,
                    className: 'case-attachment-list__column-date custom-table__cell',
                    render: ({ _createdAt = 0 }): ReactElement => <FormatDate date={_createdAt} />,
                },
                {
                    className: 'case-attachment-list__column-download custom-table__cell',
                    render: ({ _id }): ReactElement => (
                        <ActionsList
                            actionList={{
                                copy: {
                                    label: i('general.copy-url'),
                                    icon: <ICONS.CopyOutlined />,
                                    query: () => copyToClipboard(getDownloadUrl(_id), i('notification.copy')),
                                },
                                export: {
                                    label: i('general.download'),
                                    icon: <ICONS.DownloadOutlined />,
                                    CustomComponent: (triggerElem): ReactElement => (
                                        <ExternalLink url={getDownloadUrl(_id)}>{triggerElem}</ExternalLink>
                                    ),
                                },
                                delete: {
                                    label: i('general.remove'),
                                    message: i('attachment.action.confirm.delete'),
                                    hidden: !hasPermissions,
                                    icon: <ICONS.DeleteIcon />,
                                    confirm: true,
                                    query: () => {
                                        onDelete({ id: _id });
                                    },
                                },
                            }}
                            scope="single"
                            isIcon
                        />
                    ),
                },
            ]),
        [hasPermissions, onDelete, getDownloadUrl, i],
    );

    return (
        <THList.Content
            dataSource={dataSource}
            columns={columns}
            rowKey="_id"
            emptyPlaceholder={<DisplayEmptyData spin={status === 'loading'} label={i('case.attachment.none')} />}
        />
    );
};
